import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  Award, 
  Shield, 
  Phone, 
  Mail, 
  CheckCircle, 
  Wrench, 
  Star,
  Truck,
  HeadphonesIcon,
  TestTube
} from "lucide-react";

export const AboutSection = () => {
  const gammes = [
    {
      name: "ElitePrecision",
      subtitle: "pour les carreleurs exigeants",
      description: "Destinée à la pose de grès cérame pleine masse, faïence et grès, la gamme ElitePrecision offre des coupes ultra-nettes sans éclats, une tenue géométrique qui limite le voilage et une usure maîtrisée pour réduire le coût au mètre coupé.",
      color: "bg-blue-50 border-blue-200",
      icon: "🎯"
    },
    {
      name: "ElitePro",
      subtitle: "pour les maçons et le gros œuvre",
      description: "Conçue pour la découpe intensive (parpaing, briques, béton), la gamme ElitePro privilégie la robustesse, l'enlèvement de matière rapide et une dissipation thermique renforcée pour tenir les cadences des chantiers lourds.",
      color: "bg-orange-50 border-orange-200",
      icon: "🏗️"
    },
    {
      name: "Disque spécial Taille de Pierre",
      subtitle: "pour la pierre naturelle",
      description: "Dédié à la pierre naturelle (calcaire, granit, marbre), ce disque maximise la résistance à l'abrasion et la longévité des segments pour les opérations de façonnage et de sculpture.",
      color: "bg-gray-50 border-gray-200",
      icon: "🗿"
    },
    {
      name: "Disque TP (Travaux Publics)",
      subtitle: "pour les usages intensifs",
      description: "Pensé pour les usages intensifs (béton armé, enrobés, démontages), il combine rendement matière élevé et solidité adaptée aux conditions sévères des chantiers publics.",
      color: "bg-yellow-50 border-yellow-200",
      icon: "🚧"
    }
  ];

  const materielPro = [
    "Laser topo (nivellement, implantation)",
    "Électroportatif (meuleuses, perforateurs, scies portatives)",
    "Échafaudages et solutions d'accès",
    "Bétonnières et matériels de malaxage"
  ];

  const avantages = [
    {
      icon: <Shield className="h-6 w-6 text-orange-500" />,
      title: "Garanties renforcées",
      description: "Remplacement par du neuf en cas de défaut, prise en charge des frais aller/retour selon nos conditions pro."
    },
    {
      icon: <HeadphonesIcon className="h-6 w-6 text-orange-500" />,
      title: "Conseil personnalisé PRO → PRO",
      description: "Assistance téléphonique par des experts du bâtiment pour choisir le disque ou le matériel adapté à votre chantier."
    },
    {
      icon: <TestTube className="h-6 w-6 text-orange-500" />,
      title: "Disque test",
      description: "Possibilité d'essai en conditions réelles avant commande en volume (offre d'essai ElitePrecision / ElitePro)."
    },
    {
      icon: <Truck className="h-6 w-6 text-orange-500" />,
      title: "SAV structuré & suivi de commande",
      description: "Livraison sur chantier ou à l'adresse de votre choix, et support rapide en cas de besoin."
    }
  ];

  const services = [
    "Commande centralisée et livraison adaptée au chantier",
    "Fiches techniques et préconisations RPM/épaisseur par application",
    "Formations et démonstrations techniques organisables pour vos équipes",
    "Fourniture d'équipements complémentaires sur demande"
  ];

  return (
    <section id="apropos" className="py-20 bg-white scroll-mt-20">
      <div className="container mx-auto px-4">
        
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">À propos — OutilMatPro</h2>
          <p className="text-xl text-orange-600 font-semibold mb-6">
            Disques diamant & matériel pro pour les professionnels du bâtiment
          </p>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-700 leading-relaxed">
              Bonjour et merci de votre visite. Chez OutilMatPro, nous sommes spécialisés dans le matériel et l'outillage professionnel — avec un focus fort sur les disques diamant conçus pour la performance, la durabilité et la sécurité sur chantier. Nous fournissons aux artisans et aux PME des solutions éprouvées, pensées <strong className="text-orange-600">pro → pro</strong>, pour que vos chantiers avancent vite et bien.
            </p>
          </div>
        </div>

        {/* Nos gammes principales */}
        <div id="gammes-principales" className="mb-20 scroll-mt-20 animate-in fade-in duration-700">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-900 animate-in slide-in-from-bottom-4 duration-500">Nos gammes principales</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {gammes.map((gamme, index) => (
              <Card key={index} className={`${gamme.color} hover:shadow-lg transition-all duration-300 hover:scale-105 animate-in slide-in-from-bottom-8 delay-${index * 100}`}>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-3">
                    <span className="text-2xl">{gamme.icon}</span>
                    <div>
                      <div className="text-xl font-bold text-gray-900">{gamme.name}</div>
                      <div className="text-sm text-gray-600 font-medium">{gamme.subtitle}</div>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 leading-relaxed mb-4">{gamme.description}</p>
                  {gamme.name === "ElitePrecision" && (
                    <Button 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => window.location.href = 'https://rswxq4e4jh.skywork.website/'}
                    >
                      Découvrir ElitePrecision
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Matériel professionnel sur demande */}
        <div className="mb-20">
          <Card className="bg-gray-50">
            <CardHeader>
              <CardTitle className="flex items-center space-x-3">
                <Wrench className="h-6 w-6 text-orange-500" />
                <span className="text-2xl text-gray-900">Matériel professionnel sur demande</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 mb-6">
                En plus de nos disques diamant, nous pouvons fournir sur demande un large panel d'équipements professionnels pour équiper vos chantiers :
              </p>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {materielPro.map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 italic">
                Nous assurons la sélection de matériel adapté à l'usage pro, la disponibilité en volumes chantier et des conseils d'usage pour optimiser sécurité et rendement.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Validation terrain & partenariats */}
        <div className="mb-20">
          <Card className="bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
            <CardHeader>
              <CardTitle className="flex items-center space-x-3">
                <Award className="h-6 w-6 text-orange-500" />
                <span className="text-2xl text-gray-900">Validation terrain & partenariats</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">
                Nous ne travaillons pas dans l'isolement : nos produits sont développés et testés sur le terrain. OutilMatPro est fier de collaborer avec un artisan élu <strong className="text-orange-600">« Meilleur Artisan de France »</strong>, qui apporte son expertise aux essais, aux réglages techniques et aux préconisations d'utilisation. Résultat : des produits validés en conditions réelles, et des recommandations pratiques issues du terrain.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Ce qui nous distingue */}
        <div className="mb-20">
          <h3 className="text-3xl font-bold text-center mb-12 text-gray-900">Ce qui nous distingue</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {avantages.map((avantage, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      {avantage.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-lg text-gray-900 mb-2">{avantage.title}</h4>
                      <p className="text-gray-700">{avantage.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Logistique & services complémentaires */}
        <div className="mb-20">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-3">
                <Truck className="h-6 w-6 text-orange-500" />
                <span className="text-2xl text-gray-900">Logistique & services complémentaires</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                {services.map((service, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{service}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Contact final */}
        <div className="text-center">
          <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white max-w-4xl mx-auto">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4">
                Souhaitez-vous un diagnostic matériel gratuit, recevoir un disque test ElitePrecision/ElitePro ou obtenir la fiche technique d'un modèle ?
              </h3>
              <div className="mb-6">
                <p className="text-xl font-semibold mb-2">Contactez notre équipe commerciale</p>
                <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 md:space-x-8">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-5 w-5" />
                    <span className="font-semibold">06 61 01 84 89</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-5 w-5" />
                    <span className="font-semibold">outilmatpro34@yahoo.fr</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col md:flex-row justify-center space-y-3 md:space-y-0 md:space-x-4">
                <Button 
                  variant="secondary" 
                  className="bg-white text-orange-600 hover:bg-gray-100"
                  onClick={() => window.location.href = 'tel:0661018489'}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Appeler maintenant
                </Button>
                <Button 
                  variant="secondary" 
                  className="bg-white text-orange-600 hover:bg-gray-100"
                  onClick={() => window.location.href = 'mailto:outilmatpro34@yahoo.fr'}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Envoyer un email
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

      </div>
    </section>
  );
};